/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable jsx-a11y/label-has-associated-control */
import React, { useState, useEffect, useRef } from 'react'
import { Text } from '@tremor/react'
import { CircularProgress } from '@material-ui/core'

import moment from 'config/moment'
import { useApiCall } from 'hooks/useApi'

import { useLoadingTracker } from '../../hooks/useLoadingTracker'
import { MetricCard } from '../../components/MetricCard'
import { getFrequencyByLevel, FrequencyByLevelStats, FrequencyByLevelParams } from './api'
import { CustomCheckbox } from '../../components/CustomCheckbox'
import { montserratStyle } from '../../styles'

interface FrequencyByLevelProps {
  // eslint-disable-next-line @typescript-eslint/member-delimiter-style
  period: { month: number; year: number }
  // eslint-disable-next-line react/require-default-props
  storeId?: number
  compara: boolean
  // eslint-disable-next-line react/require-default-props
  compareStoreId?: number
  trigger: number
  // eslint-disable-next-line react/require-default-props
  onLoaded?: () => void
}

const FrequencyByLevel: React.FC<FrequencyByLevelProps> = ({
  period,
  storeId,
  compara,
  compareStoreId,
  trigger,
  onLoaded,
}) => {
  const [showAllUsers, setShowAllUsers] = useState(false)
  const [showRecurrent, setShowRecurrent] = useState(true)
  const [data, setData] = useState<FrequencyByLevelStats | null>(null)
  const [data2, setData2] = useState<FrequencyByLevelStats | null>(null)
  const [isLoadingFilter, setIsLoadingFilter] = useState(false)
  const canFetchRef = useRef(false)
  const skipFilterFetchRef = useRef(true)
  const [statsCall, statsLoading] = useApiCall<
    FrequencyByLevelParams,
    { data: FrequencyByLevelStats }
  >(getFrequencyByLevel)

  // Track loading state
  useLoadingTracker({ isLoading: statsLoading || isLoadingFilter, componentId: 'FrequencyByLevel' })

  const fetchData = async (callOnLoaded = false) => {
    try {
      setIsLoadingFilter(true)
      const startDate = moment()
        .year(period.year)
        .month(period.month)
        .startOf('month')
        .format('YYYY-MM-DD')
      const endDate = moment()
        .year(period.year)
        .month(period.month)
        .endOf('month')
        .format('YYYY-MM-DD')

      const userType = showRecurrent ? 'recurrent' : 'all'

      const response1 = await statsCall({
        startDate,
        endDate,
        storeId,
        userType,
      })

      if (response1?.data) {
        setData(response1.data)
      }

      if (compara) {
        const response2 = await statsCall({
          startDate,
          endDate,
          storeId: compareStoreId,
          userType,
        })

        if (response2?.data) {
          setData2(response2.data)
        }
      } else {
        setData2(null)
      }
    } catch (error) {
      console.error('Error fetching frequency by level statistics:', error)
    } finally {
      setIsLoadingFilter(false)
      if (callOnLoaded && onLoaded) {
        onLoaded()
      }
    }
  }

  // Carga inicial / Apply: solo cuando trigger > 0 (pipeline secuencial)
  useEffect(() => {
    if (trigger > 0) {
      canFetchRef.current = true
      fetchData(true)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trigger])

  // Filtros locales: re-fetch aunque el step haya avanzado y trigger sea 0
  useEffect(() => {
    if (skipFilterFetchRef.current) {
      skipFilterFetchRef.current = false
      return
    }
    if (!canFetchRef.current) return
    fetchData(false)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showRecurrent, showAllUsers])

  const calculateDiff = (current: number, previous: number) => {
    if (previous === 0) return 0
    return ((current - previous) / previous) * 100
  }

  const formatStats = (current: FrequencyByLevelStats, previous?: FrequencyByLevelStats) => {
    if (!previous) {
      return current.byLevel.map(level => ({
        title: level.nivel,
        value: level.value,
        prev: 0,
        diff: 0,
        isPositive: true,
        detail: `0 de la sucursal ${storeId === undefined ? 'Todas' : storeId}`,
      }))
    }

    return current.byLevel.map((level, index) => {
      const prevValue = previous.byLevel[index]?.value || 0
      const diff = calculateDiff(level.value, prevValue)

      return {
        title: level.nivel,
        value: level.value,
        prev: prevValue,
        diff: Math.round(diff),
        isPositive: level.value >= prevValue,
        detail: `${prevValue.toFixed(2)} de la sucursal ${
          compareStoreId === undefined ? 'Todas' : compareStoreId
        }`,
      }
    })
  }

  const handleAllUsersChange = (checked: boolean) => {
    if (checked) {
      setShowAllUsers(true)
      setShowRecurrent(false)
    } else {
      // Si intenta desmarcar, asegurar que el otro esté marcado
      if (!showRecurrent) {
        setShowRecurrent(true)
      }
      setShowAllUsers(false)
    }
  }

  const handleRecurrentChange = (checked: boolean) => {
    if (checked) {
      setShowRecurrent(true)
      setShowAllUsers(false)
    } else {
      // Si intenta desmarcar, asegurar que el otro esté marcado
      if (!showAllUsers) {
        setShowAllUsers(true)
      }
      setShowRecurrent(false)
    }
  }

  const isLoading = statsLoading || isLoadingFilter

  if (!data || isLoading) {
    return (
      <div className="bg-white rounded-lg shadow p-6 flex flex-col w-full">
        <div className="flex flex-row justify-between items-center mb-4">
          <span className="text-base font-bold" style={{ ...montserratStyle }}>
            Frecuencias por nivel
          </span>
          <div className="flex gap-6 items-center">
            <label className="flex items-center gap-2 text-xs font-medium">
              <CustomCheckbox
                checked={showAllUsers}
                onChange={e => handleAllUsersChange(e.target.checked)}
                className="w-4 h-4 rounded border border-gray-400"
                disabled={isLoading}
              />
              Todos los usuarios
            </label>
            <label className="flex items-center gap-2 text-xs font-medium">
              <CustomCheckbox
                checked={showRecurrent}
                onChange={e => handleRecurrentChange(e.target.checked)}
                className="w-4 h-4 rounded border border-gray-400"
                disabled={isLoading}
              />
              Usuarios recurrentes
            </label>
          </div>
        </div>
        <div className="flex items-center justify-center h-32">
          {isLoading ? (
            <CircularProgress size={40} />
          ) : (
            <Text>No hay datos disponibles para mostrar en este momento.</Text>
          )}
        </div>
      </div>
    )
  }

  const displayData = formatStats(data, data2 || undefined)

  return (
    <div className="bg-white rounded-lg shadow p-6 flex flex-col w-full">
      <div className="flex flex-row justify-between items-center mb-4">
        <span className="text-base font-bold" style={{ ...montserratStyle }}>
          Frecuencias por nivel
        </span>
        <div className="flex gap-6 items-center">
          <label className="flex items-center gap-2 text-xs font-medium">
            <CustomCheckbox
              checked={showAllUsers}
              onChange={e => handleAllUsersChange(e.target.checked)}
              className="w-4 h-4 rounded border border-gray-400"
            />
            Todos los usuarios
          </label>
          <label className="flex items-center gap-2 text-xs font-medium">
            <CustomCheckbox
              checked={showRecurrent}
              onChange={e => handleRecurrentChange(e.target.checked)}
              className="w-4 h-4 rounded border border-gray-400"
            />
            Usuarios recurrentes
          </label>
        </div>
      </div>
      {/* Layout fluido: las cards crecen para completar la fila */}
      <div className="grid gap-6 grid-cols-[repeat(auto-fit,minmax(260px,1fr))]">
        {displayData.map(item => (
          <div key={item.title} className="border border-gray-200 rounded-xl p-4 bg-white h-full">
            <MetricCard
              title={item.title}
              value={item.value}
              prev={item.prev}
              diff={item.diff}
              isPositive={item.isPositive}
              showComparison={false}
              comparisonMonth=""
              comparisonYear=""
              className="h-full"
            >
              <span className="text-xs text-gray-600">({item.detail})</span>
            </MetricCard>
          </div>
        ))}
      </div>
    </div>
  )
}

export default FrequencyByLevel
